import { useState } from "react";
import type { FormEvent, ReactNode } from "react";
import { browserSupportsWebAuthn, startAuthentication } from "@simplewebauthn/browser";
import { KeyRound, Fingerprint, LifeBuoy, ShieldCheck } from "lucide-react";
import { completeWithBackupCode, completeWithCode, completeWithPasskey, passkeyLoginOptions } from "../../api/auth.js";
import type { SecondFactorOutcome } from "../../api/auth.js";
import { AuthFlowError, isPasskeyCancel } from "../../app/authFlow.js";
import { ApiClientError } from "../../api/http.js";
import { isDesktop } from "../../app/platform.js";
import { isCompleteOtp, normalizeOtp } from "../../lib/otp.js";
import { Alert, Button, Segmented } from "../../ui/primitives.js";
import type { SegmentOption } from "../../ui/primitives.js";
import { Field, OtpInput, TextInput } from "../../ui/forms.js";

type Method = "code" | "passkey" | "backup";

interface SecondFactorStepProps {
  readonly ticket: string;
  /** Runs after the server accepted the factor; errors it throws are shown in the step. */
  readonly onVerified: (outcome: SecondFactorOutcome) => Promise<void>;
}

function messageFor(err: unknown, method: Method): string {
  if (err instanceof AuthFlowError) {
    return err.message;
  }
  if (err instanceof ApiClientError) {
    if (err.status === 429) {
      return "Zu viele Versuche. Bitte warte einen Moment.";
    }
    if (err.status === 401 || err.status === 404) {
      if (method === "passkey") {
        return "Der Passkey wurde nicht akzeptiert. Versuche es erneut oder wähle eine andere Methode.";
      }
      return method === "backup"
        ? "Dieser Backup-Code ist ungültig oder bereits verbraucht."
        : "Der Code ist ungültig, abgelaufen oder wurde schon verwendet. Warte auf den nächsten Code.";
    }
    return err.message;
  }
  if (isPasskeyCancel(err)) {
    return "Die Passkey-Abfrage wurde abgebrochen.";
  }
  return "Die Bestätigung ist fehlgeschlagen. Bitte erneut versuchen.";
}

export function SecondFactorStep(props: SecondFactorStepProps): ReactNode {
  const desktop = isDesktop();
  // WebAuthn is bound to the web origin, so the desktop shell cannot answer a passkey challenge.
  const passkeysSupported = !desktop && typeof window !== "undefined" && browserSupportsWebAuthn();
  const [method, setMethod] = useState<Method>("code");
  const [code, setCode] = useState("");
  const [backup, setBackup] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | undefined>(undefined);

  async function verifyCode(value: string): Promise<void> {
    if (busy || !isCompleteOtp(value)) {
      return;
    }
    setBusy(true);
    setError(undefined);
    try {
      await props.onVerified(await completeWithCode(props.ticket, value));
    } catch (err: unknown) {
      setError(messageFor(err, "code"));
      setCode("");
    } finally {
      setBusy(false);
    }
  }

  async function verifyBackup(event: FormEvent<HTMLFormElement>): Promise<void> {
    event.preventDefault();
    const value = backup.trim();
    if (busy || value.length < 4) {
      return;
    }
    setBusy(true);
    setError(undefined);
    try {
      await props.onVerified(await completeWithBackupCode(props.ticket, value));
    } catch (err: unknown) {
      setError(messageFor(err, "backup"));
    } finally {
      setBusy(false);
    }
  }

  async function verifyPasskey(): Promise<void> {
    if (busy) {
      return;
    }
    setBusy(true);
    setError(undefined);
    try {
      const options = await passkeyLoginOptions(props.ticket);
      const credential = await startAuthentication({ optionsJSON: options });
      await props.onVerified(await completeWithPasskey(props.ticket, credential));
    } catch (err: unknown) {
      setError(messageFor(err, "passkey"));
    } finally {
      setBusy(false);
    }
  }

  const codeOption: SegmentOption<Method> = { value: "code", label: "Code", icon: ShieldCheck };
  const passkeyOption: SegmentOption<Method> = { value: "passkey", label: "Passkey", icon: Fingerprint };
  const backupOption: SegmentOption<Method> = { value: "backup", label: "Backup", icon: LifeBuoy };
  const options: ReadonlyArray<SegmentOption<Method>> = passkeysSupported
    ? [codeOption, passkeyOption, backupOption]
    : [codeOption, backupOption];

  return (
    <div className="flex flex-col gap-6">
      <div className="flex flex-col gap-4">
        <span className="grid size-10 place-items-center rounded-lg border border-line bg-ink-3 text-flow">
          <KeyRound size={18} aria-hidden="true" />
        </span>
        <div>
          <h2 className="font-display text-xl font-bold tracking-[-0.01em] text-foam">Zweiten Faktor bestätigen</h2>
          <p className="mt-1.5 text-sm leading-relaxed text-mist">
            Gib den Code aus deiner Authenticator-App ein, bestätige mit einem Passkey oder nutze einen Backup-Code.
          </p>
        </div>
      </div>

      <Segmented label="Methode wählen" value={method} options={options} onChange={(value: Method): void => { setMethod(value); setError(undefined); }} className="w-full justify-between" />

      {method === "code" ? (
        <div className="flex flex-col gap-5">
          <OtpInput
            label="Sechsstelliger Code"
            value={code}
            autoFocus
            disabled={busy}
            invalid={error !== undefined}
            onChange={(value: string): void => {
              setCode(normalizeOtp(value));
              setError(undefined);
            }}
            onComplete={(value: string): void => {
              void verifyCode(value);
            }}
          />
          <Button size="lg" className="w-full" loading={busy} disabled={!isCompleteOtp(code)} onClick={(): void => void verifyCode(code)}>
            Bestätigen
          </Button>
        </div>
      ) : null}

      {method === "passkey" ? (
        <div className="flex flex-col gap-5">
          <p className="text-sm leading-relaxed text-mist">Bestätige mit deinem Gerät, Sicherheitsschlüssel oder Passkey-Manager.</p>
          <Button size="lg" className="w-full" icon={Fingerprint} loading={busy} onClick={(): void => void verifyPasskey()}>
            Mit Passkey bestätigen
          </Button>
        </div>
      ) : null}

      {method === "backup" ? (
        <form onSubmit={(event): void => void verifyBackup(event)} className="flex flex-col gap-5">
          <Field id="backup-code" label="Backup-Code" hint="Jeder Code funktioniert nur einmal.">
            <TextInput
              id="backup-code"
              autoComplete="off"
              spellCheck={false}
              autoCapitalize="characters"
              value={backup}
              onChange={(event): void => {
                setBackup(event.target.value);
                setError(undefined);
              }}
              placeholder="xxxx-xxxx"
              className="font-mono tracking-wider"
            />
          </Field>
          <Button type="submit" size="lg" className="w-full" loading={busy} disabled={backup.trim().length < 4}>
            Mit Backup-Code anmelden
          </Button>
        </form>
      ) : null}

      {desktop ? (
        <p className="text-[0.8125rem] leading-relaxed text-mist-2">
          Passkeys funktionieren nur in der Web-App. Nutze hier den Code aus deiner Authenticator-App oder einen Backup-Code.
        </p>
      ) : null}

      {error !== undefined ? <Alert tone="danger">{error}</Alert> : null}
    </div>
  );
}
